import { create } from 'zustand'
import dayjs from 'dayjs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useDatePicker } from '@/components/MyDatePicker'

// 对比模式状态管理store
export const useContrastModeStore = create((set) => ({
  enabled: false,
  type: null, // 'yoy' | 'mom' | 'custom' | null
  customYear: null,
  customMonth: null,
  // 控制显示选项
  showContrastValue: true, // 显示原始值（对比值）
  showDiff: true, // 显示差异值
  showDiffPercent: true, // 显示差异百分比
  setEnabled: (enabled) => set({ enabled }),
  setType: (type) => set({ type, enabled: type !== null }),
  setCustomYear: (year) => set({ customYear: year }),
  setCustomMonth: (month) => set({ customMonth: month }),
  setShowContrastValue: (show) => set({ showContrastValue: show }),
  setShowDiff: (show) => set({ showDiff: show }),
  setShowDiffPercent: (show) => set({ showDiffPercent: show }),
  reset: () => set({ enabled: false, type: null, customYear: null, customMonth: null }),
}))

const ContrastModeSelector = ({ className, datePickerStore }) => {
  const enabled = useContrastModeStore((state) => state.enabled)
  const type = useContrastModeStore((state) => state.type)
  const customYear = useContrastModeStore((state) => state.customYear)
  const customMonth = useContrastModeStore((state) => state.customMonth)
  const setType = useContrastModeStore((state) => state.setType)
  const setCustomYear = useContrastModeStore((state) => state.setCustomYear)
  const setCustomMonth = useContrastModeStore((state) => state.setCustomMonth)

  const currentYear = dayjs().year()
  const minYear = 2020
  // 支持传入自定义的 datePickerStore，默认使用 useDatePicker
  const dateStore = datePickerStore || useDatePicker
  const endDate = dateStore((state) => state.endDate)

  const handleTypeChange = (value) => {
    if (value === 'off') {
      setType(null)
    } else {
      setType(value)
      // 如果是自定义模式，设置默认值为当前选择的去年同期
      if (value === 'custom') {
        if (!customYear || !customMonth) {
          // 根据当前选择的 endDate 计算去年同期
          if (endDate) {
            const endDateObj = dayjs(String(endDate), 'YYYYMMDD')
            const lastYearEndDateObj = endDateObj.subtract(1, 'year')
            const defaultYear = lastYearEndDateObj.year()
            const defaultMonth = lastYearEndDateObj.month() + 1 // dayjs month is 0-based
            
            if (!customYear) {
              setCustomYear(defaultYear)
            }
            if (!customMonth) {
              setCustomMonth(defaultMonth)
            }
          } else {
            // 如果没有 endDate，使用默认值
            if (!customYear) {
              setCustomYear(currentYear - 1)
            }
            if (!customMonth) {
              setCustomMonth(1)
            }
          }
        }
      }
    }
  }

  const handleYearChange = (e) => {
    const value = e.target.value
    if (value === '') {
      setCustomYear(null)
      return
    }
    const year = parseInt(value, 10)
    if (!isNaN(year) && year >= minYear && year <= currentYear) {
      setCustomYear(year)
    }
  }

  const handleMonthChange = (e) => {
    const value = e.target.value
    if (value === '') {
      setCustomMonth(null)
      return
    }
    const month = parseInt(value, 10)
    if (!isNaN(month) && month >= 1 && month <= 12) {
      setCustomMonth(month)
    }
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Select value={type || 'off'} onValueChange={handleTypeChange}>
        <SelectTrigger className="w-auto min-w-[110px]">
          <SelectValue placeholder="对比模式" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="off">关闭对比</SelectItem>
          <SelectItem value="yoy">去年同期</SelectItem>
          <SelectItem value="mom">上月同期</SelectItem>
          <SelectItem value="custom">自定义</SelectItem>
        </SelectContent>
      </Select>

      {type === 'custom' && (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            placeholder="年份"
            value={customYear || ''}
            onChange={handleYearChange}
            min={minYear}
            max={currentYear}
            className="w-[100px]"
          />
          <Select
            value={customMonth ? String(customMonth) : ''}
            onValueChange={(value) => setCustomMonth(parseInt(value, 10))}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="月份" />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                <SelectItem key={month} value={String(month)}>
                  {month}月
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  )
}

export default ContrastModeSelector

